import React, { useEffect, useMemo, useState } from "react";

import { getReservations } from "../../api/reservationsService";
import {
   getMyPracticalExamHistory,
   getMyPracticalExamReservation,
} from "../../api/practicalExamSlotsService";

const PRACTICAL_FINAL_STATUSES = new Set([
   "PASSED",
   "PROMOTED",
   "PROMOVAT",
   "ADMIS",
   "FAILED",
   "RESPINS",
   "ABSENT",
   "CANCELLED",
   "CANCELED",
   "ANULAT",
]);

const STATUS_LABELS = {
   SCHEDULED: "Programat",
   PASSED: "Admis",
   PROMOTED: "Admis",
   PROMOVAT: "Admis",
   ADMIS: "Admis",
   FAILED: "Respins",
   RESPINS: "Respins",
   ABSENT: "Absent",
   CANCELLED: "Anulat",
   CANCELED: "Anulat",
   ANULAT: "Anulat",
};

function normalizeItems(raw) {
   if (Array.isArray(raw)) return raw;
   if (!raw || typeof raw !== "object") return [];

   const candidates = [
      raw.items,
      raw.data,
      raw.results,
      raw.rows,
      raw.reservations,
      raw.list,
   ];

   for (const c of candidates) {
      if (Array.isArray(c)) return c;
      if (c && typeof c === "object") {
         if (Array.isArray(c.items)) return c.items;
         if (Array.isArray(c.data)) return c.data;
         if (Array.isArray(c.results)) return c.results;
         if (Array.isArray(c.rows)) return c.rows;
         if (Array.isArray(c.reservations)) return c.reservations;
         if (Array.isArray(c.list)) return c.list;
      }
   }

   return [];
}

function toMs(val) {
   if (!val) return 0;
   const d = new Date(val);
   const ms = d.getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function readFirst(source, keys, fallback = null) {
   if (!source || typeof source !== "object") return fallback;

   for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
   }

   return fallback;
}

function readStart(item) {
   return (
      item?.startTime ??
      item?.start_time ??
      item?.start ??
      item?.dateTime ??
      item?.datetime ??
      item?.date ??
      item?.reservation?.startTime ??
      item?.reservation?.start_time ??
      item?.reservation?.start ??
      item?.reservation?.dateTime ??
      item?.reservation?.datetime ??
      item?.reservation?.date ??
      null
   );
}

function readReservationId(item) {
   return readFirst(
      item,
      ["id", "_id", "reservationId", "reservation_id", "reservationID"],
      readFirst(item?.reservation, [
         "id",
         "_id",
         "reservationId",
         "reservation_id",
         "reservationID",
      ]),
   );
}

function normalizeStatus(status, fallback = "") {
   return String(status || fallback)
      .trim()
      .toUpperCase();
}

function readRegularStatus(item) {
   return normalizeStatus(
      readFirst(
         item,
         [
            "status",
            "state",
            "reservationStatus",
            "reservation_status",
            "isCancelled",
            "isCanceled",
            "is_cancelled",
            "is_canceled",
         ],
         readFirst(item?.reservation, [
            "status",
            "state",
            "reservationStatus",
            "reservation_status",
            "isCancelled",
            "isCanceled",
            "is_cancelled",
            "is_canceled",
         ]),
      ),
   );
}

function isCancelledRegular(item) {
   const status = readRegularStatus(item);
   return (
      status === "CANCELLED" ||
      status === "CANCELED" ||
      status === "ANULAT" ||
      item?.isCancelled === true ||
      item?.isCanceled === true ||
      item?.is_cancelled === true ||
      item?.is_canceled === true ||
      item?.canceledAt ||
      item?.cancelledAt ||
      item?.reservation?.isCancelled === true ||
      item?.reservation?.isCanceled === true ||
      item?.reservation?.is_cancelled === true ||
      item?.reservation?.is_canceled === true ||
      item?.reservation?.canceledAt ||
      item?.reservation?.cancelledAt
   );
}

function readRawDateTimeParts(val) {
   const text = String(val || "").trim();
   if (!text) return null;

   const match = text.match(
      /(\d{4})-(\d{2})-(\d{2})(?:[T\s]+)(\d{2}):(\d{2})/,
   );
   if (!match) return null;

   const [, year, month, day, hour, minute] = match;
   return {
      year,
      month,
      day,
      hour,
      minute,
   };
}

function fmtDate(val) {
   if (!val) return "—";
   const raw = readRawDateTimeParts(val);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}`;

   const d = new Date(val);
   if (Number.isNaN(d.getTime())) return String(val);
   return d.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
   });
}

function fmtTime(val) {
   if (!val) return "—";
   const raw = readRawDateTimeParts(val);
   if (raw) return `${raw.hour}:${raw.minute}`;

   const d = new Date(val);
   if (Number.isNaN(d.getTime())) return "—";
   return d.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
   });
}

function readSector(item) {
   const raw = item?.sector ?? item?.reservation?.sector ?? "";
   const value = String(raw || "").trim();
   return value || "—";
}

function readGearbox(item) {
   const raw = item?.gearbox ?? item?.reservation?.gearbox ?? "";
   const value = String(raw || "").trim();
   return value || "—";
}

function unwrapOne(raw) {
   if (!raw || typeof raw !== "object") return null;
   if (raw.data === null || raw.reservation === null || raw.item === null) {
      return null;
   }
   if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
      return raw.data;
   }
   if (
      raw.reservation &&
      typeof raw.reservation === "object" &&
      !Array.isArray(raw.reservation)
   ) {
      return raw.reservation;
   }
   if (raw.item && typeof raw.item === "object" && !Array.isArray(raw.item)) {
      return raw.item;
   }
   if (raw.id != null || raw._id != null || raw.slotId != null) return raw;
   return null;
}

function readPracticalSlot(reservation) {
   return (
      reservation?.slot ??
      reservation?.practicalExamSlot ??
      reservation?.examSlot ??
      reservation?.PracticalExamSlot ??
      reservation?.practical_exam_slot ??
      null
   );
}

function readPracticalDate(reservation) {
   const slot = readPracticalSlot(reservation);
   const date = readFirst(
      reservation,
      [
         "dateKey",
         "date",
         "slotDate",
         "slot_date",
         "examDate",
         "exam_date",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ],
      readFirst(slot, [
         "dateKey",
         "date",
         "slotDate",
         "slot_date",
         "examDate",
         "exam_date",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ]),
   );

   return typeof date === "string" && date.includes("T")
      ? date.slice(0, 10)
      : date
        ? String(date).slice(0, 10)
        : "";
}

function readPracticalTime(reservation) {
   const slot = readPracticalSlot(reservation);
   const time = readFirst(
      reservation,
      [
         "timeKey",
         "time",
         "slotTime",
         "slot_time",
         "examTime",
         "exam_time",
         "startTime",
         "start_time",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ],
      readFirst(slot, [
         "timeKey",
         "time",
         "slotTime",
         "slot_time",
         "examTime",
         "exam_time",
         "startTime",
         "start_time",
         "startAt",
         "start_at",
         "startsAt",
         "starts_at",
      ]),
   );

   if (typeof time === "string") {
      const isoMatch = time.match(/[T\s](\d{2}):(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;
      const timeMatch = time.match(/^(\d{1,2}):(\d{2})/);
      if (timeMatch) {
         return `${String(Number(timeMatch[1])).padStart(2, "0")}:${timeMatch[2]}`;
      }
   }

   return time ? String(time) : "";
}

function readPracticalStatus(reservation) {
   return normalizeStatus(
      readFirst(
         reservation,
         ["status", "result", "examStatus", "exam_status", "examResult", "verdict"],
         "SCHEDULED",
      ),
      "SCHEDULED",
   );
}

function readPracticalSector(reservation) {
   const slot = readPracticalSlot(reservation);
   return String(readFirst(reservation, ["sector"], readFirst(slot, ["sector"], "—")));
}

function readPracticalGearbox(reservation) {
   const slot = readPracticalSlot(reservation);
   return String(
      readFirst(reservation, ["gearbox", "cutie"], readFirst(slot, ["gearbox"], "—")),
   );
}

function isIgnorablePracticalHistoryError(error) {
   const message = String(error?.message || "");
   return (
      message.includes("getMyPracticalExamHistory 400") ||
      message.includes("numeric string is expected")
   );
}

function isNoActivePracticalReservationError(error) {
   const message = String(error?.message || "").toLowerCase();
   return (
      message.includes("getmypracticalexamreservation 404") ||
      message.includes("not found") ||
      message.includes("nu există") ||
      message.includes("nu exista") ||
      message.includes("negăsit") ||
      message.includes("negasit")
   );
}

function practicalStartValue(reservation) {
   const date = readPracticalDate(reservation);
   const time = readPracticalTime(reservation) || "00:00";
   return date ? `${date}T${time}:00` : "";
}

function buildRegularItem(item, index) {
   const id = readReservationId(item);
   const start = readStart(item);
   const cancelled = isCancelledRegular(item);

   return {
      kind: "lesson",
      id,
      key: id != null ? `lesson-${String(id)}` : `lesson-${String(start || "x")}-${index}`,
      date: fmtDate(start),
      time: fmtTime(start),
      extra: `${readSector(item)} · ${readGearbox(item)}`,
      sortMs: toMs(start),
      inactive: cancelled,
      statusLabel: cancelled ? "Anulat" : "",
      canCancel: false,
      raw: item,
   };
}

function buildPracticalItem(reservation, index, options = {}) {
   if (!reservation) return null;

   const id = readReservationId(reservation);
   const status = readPracticalStatus(reservation);
   const finalStatus = PRACTICAL_FINAL_STATUSES.has(status);
   const start = practicalStartValue(reservation);
   const statusLabel = STATUS_LABELS[status] || status || "Programat";

   return {
      kind: "practicalExam",
      id,
      key:
         id != null
            ? `practical-${String(id)}`
            : `practical-${String(start || "x")}-${index}`,
      date: fmtDate(start),
      time: fmtTime(start),
      extra: `${readPracticalSector(reservation)} · ${readPracticalGearbox(reservation)}`,
      sortMs: toMs(start),
      inactive: finalStatus,
      statusLabel,
      status,
      current: Boolean(options.current),
      canCancel: false,
      raw: reservation,
   };
}

function isSameReservationItem(a, b) {
   if (!a || !b || a.kind !== b.kind) return false;
   if (a.id != null && b.id != null) return String(a.id) === String(b.id);

   return (
      a.date === b.date &&
      a.time === b.time &&
      a.extra === b.extra &&
      a.statusLabel === b.statusLabel
   );
}

function ReservationCard({ item }) {
   return (
      <article
         className={
            "studentReservationsPopup__card" +
            (item.current ? " studentReservationsPopup__card--current" : "") +
            (item.kind === "practicalExam" && item.status
               ? ` studentReservationsPopup__card--status-${String(item.status).toLowerCase()}`
               : "") +
            (item.inactive ? " studentReservationsPopup__card--inactive" : "")
         }
      >
         <p className="studentReservationsPopup__date">
            {item.date}
            {", "}
            <span className="studentReservationsPopup__time">
               {item.time}
            </span>
         </p>
         <p className="studentReservationsPopup__extra">
            {item.extra}
         </p>
         {item.statusLabel ? (
            <p className="studentReservationsPopup__status">
               {item.statusLabel}
            </p>
         ) : null}
      </article>
   );
}

export default function StudentReservationsPopup() {
   const [list, setList] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");

   useEffect(() => {
      let alive = true;
      (async () => {
         setLoading(true);
         setError("");
         const [regularResult, activeExamResult, examHistoryResult] =
            await Promise.allSettled([
               getReservations(),
               getMyPracticalExamReservation(),
               getMyPracticalExamHistory(),
            ]);

         if (!alive) return;

         const nextItems = [];
         const notices = [];

         if (regularResult.status === "fulfilled") {
            normalizeItems(regularResult.value).forEach((item, index) => {
               nextItems.push(buildRegularItem(item, index));
            });
         } else {
            notices.push(
               regularResult.reason?.message ||
                  "Nu am putut încărca rezervările lecțiilor.",
            );
         }

         if (activeExamResult.status === "fulfilled") {
            const activeExam = unwrapOne(activeExamResult.value);
            const built = buildPracticalItem(activeExam, 0, { current: true });
            if (built) nextItems.push(built);
         } else {
            if (!isNoActivePracticalReservationError(activeExamResult.reason)) {
               notices.push(
                  activeExamResult.reason?.message ||
                     "Nu am putut încărca programarea la Ex. practic.",
               );
            }
         }

         if (examHistoryResult.status === "fulfilled") {
            normalizeItems(examHistoryResult.value).forEach((item, index) => {
               const built = buildPracticalItem(item, index + 1);
               if (!built) return;
               if (nextItems.some((existing) => isSameReservationItem(existing, built))) {
                  return;
               }
               nextItems.push(built);
            });
         } else if (!isIgnorablePracticalHistoryError(examHistoryResult.reason)) {
            notices.push(
               examHistoryResult.reason?.message ||
                  "Nu am putut încărca istoricul Ex. practic.",
            );
         }

         setList(nextItems.filter(Boolean));
         setError(notices.join(" "));
         setLoading(false);
      })();
      return () => {
         alive = false;
      };
   }, []);

   const lessonSorted = useMemo(() => {
      const clone = Array.isArray(list)
         ? list.filter((item) => item?.kind !== "practicalExam")
         : [];
      clone.sort((a, b) => Number(a.sortMs || 0) - Number(b.sortMs || 0));
      return clone;
   }, [list]);

   const practicalItems = useMemo(() => {
      const clone = Array.isArray(list)
         ? list.filter((item) => item?.kind === "practicalExam")
         : [];
      clone.sort((a, b) => {
         if (a.current !== b.current) return a.current ? -1 : 1;
         if (a.canCancel !== b.canCancel) return a.canCancel ? -1 : 1;
         return Number(b.sortMs || 0) - Number(a.sortMs || 0);
      });
      return clone;
   }, [list]);

   const { upcoming, past } = useMemo(() => {
      const now = Date.now();
      const next = [];
      const prev = [];

      for (const item of lessonSorted) {
         const ms = Number(item.sortMs || 0);
         if (!item.inactive && ms && ms >= now) next.push(item);
         else prev.push(item);
      }

      return { upcoming: next, past: prev.reverse() };
   }, [lessonSorted]);

   return (
      <div className="studentReservationsPopup">
         <div className="studentReservationsPopup__header">
            <h3 className="studentReservationsPopup__title">
               Rezervările mele
            </h3>
         </div>

         {loading ? (
            <p className="studentReservationsPopup__state">Se încarcă...</p>
         ) : (
            <>
               {error ? (
                  <p className="studentReservationsPopup__error">{error}</p>
               ) : null}
               {!lessonSorted.length && !practicalItems.length ? (
                  <p className="studentReservationsPopup__state">
                     Nu există rezervări.
                  </p>
               ) : (
            <div className="studentReservationsPopup__content">
               {practicalItems.length ? (
                  <section className="studentReservationsPopup__section studentReservationsPopup__section--exam">
                     <div className="studentReservationsPopup__sectionHead">
                        <h4 className="studentReservationsPopup__sectionTitle">
                           Examen intern
                        </h4>
                        <span className="studentReservationsPopup__sectionCount">
                           {practicalItems.length}
                        </span>
                     </div>
                     <div className="studentReservationsPopup__grid studentReservationsPopup__grid--exam">
                        {practicalItems.map((item, idx) => (
                           <ReservationCard
                              key={`${item.key}-${idx}`}
                              item={item}
                           />
                        ))}
                     </div>
                  </section>
               ) : null}

               <section className="studentReservationsPopup__section">
                  <div className="studentReservationsPopup__sectionHead">
                     <h4 className="studentReservationsPopup__sectionTitle">
                        Urmează
                     </h4>
                     <span className="studentReservationsPopup__sectionCount">
                        {upcoming.length}
                     </span>
                  </div>
                  {upcoming.length ? (
                     <div className="studentReservationsPopup__grid">
                        {upcoming.map((item, idx) => (
                           <ReservationCard
                              key={`${item.key}-${idx}`}
                              item={item}
                           />
                        ))}
                     </div>
                  ) : (
                     <p className="studentReservationsPopup__sectionEmpty">
                        Nu ai rezervări următoare.
                     </p>
                  )}
               </section>

               <section className="studentReservationsPopup__section">
                  <div className="studentReservationsPopup__sectionHead">
                     <h4 className="studentReservationsPopup__sectionTitle">
                        Trecute
                     </h4>
                     <span className="studentReservationsPopup__sectionCount">
                        {past.length}
                     </span>
                  </div>
                  {past.length ? (
                     <div className="studentReservationsPopup__grid">
                        {past.map((item, idx) => (
                           <ReservationCard
                              key={`${item.key}-${idx}`}
                              item={item}
                           />
                        ))}
                     </div>
                  ) : (
                     <p className="studentReservationsPopup__sectionEmpty">
                        Nu ai rezervări trecute.
                     </p>
                  )}
               </section>
            </div>
               )}
            </>
         )}
      </div>
   );
}
